/**
 * Swedish copy for the beta-lane (document-IR, ADR-0099) error messages the
 * backend returns. Unknown messages pass through unchanged, so call sites
 * shared with the legacy lane keep their behaviour.
 */
import type { IrDiagnostic } from '../api.js';
import { openReviewItemsOf, signingGateText } from '../components/beta-review/review-model.js';

const OPEN_REVIEW_ITEMS_TEXT =
  'Granskningen har öppna ändringsförslag eller begäranden. Besluta om dem på granskningssidan innan avtalet kan skickas för signering.';

const IR_ERRORS: Record<string, string> = {
  ir_document_has_errors:
    'Dokumentet innehåller fel som måste åtgärdas innan det kan skickas. Se diagnostiken under fliken Avtal.',
  ir_validation_unresolved:
    'Avtalet har fel som måste rättas eller behållas med "Behåll ändå" innan det kan gå vidare. Se sammanfattningen under fliken Avtal.',
  revision_already_signed: 'Dokumentet är redan signerat och kan inte ställas ut igen.',
  ir_not_available_for_template: 'Mallen finns inte i den uppdaterade layouten.',
  not_an_autofill_field: 'Fältet fylls inte i automatiskt.',
  contract_not_in_draft: 'Avtalet är inte längre ett utkast.',
  ir_patch_conflicts:
    'Den anpassade texten har konflikter som måste lösas innan avtalet kan skickas. Öppna Anpassa text från fliken Avtal.',
  open_review_items: OPEN_REVIEW_ITEMS_TEXT,
  review_required_for_deviation:
    'Texten i avtalet är anpassad och måste granskas och godkännas innan avtalet kan skickas för signering eller exporteras. Skicka avtalet för granskning från fliken Avtal.',
};

export function irErrorText(message: string | null | undefined, fallback: string): string {
  if (!message) return fallback;
  return IR_ERRORS[message] ?? message;
}

type ErrorLike = { data?: { reason?: unknown; diagnostics?: unknown }; message?: string };

/** A procedure error as display text: its structured `reason` when set, else its message. */
export function signingErrorText(e: unknown, fallback: string): string {
  const open = openReviewItemsOf(e);
  if (open) return signingGateText(open) ?? OPEN_REVIEW_ITEMS_TEXT;
  const err = e as ErrorLike | null | undefined;
  const reason = typeof err?.data?.reason === 'string' ? err.data.reason : undefined;
  return irErrorText(reason ?? err?.message, fallback);
}

const SEVERITIES = new Set(['error', 'warning']);

/**
 * The diagnostics carried by an `ir_document_has_errors` or
 * `ir_validation_unresolved` refusal (issue-on-sign, ADR-0099 / ADR-0101). Empty for every other error.
 */
export function irDiagnosticsOf(e: unknown): IrDiagnostic[] {
  const data = (e as ErrorLike | null | undefined)?.data;
  if (
    (data?.reason !== 'ir_document_has_errors' && data?.reason !== 'ir_validation_unresolved') ||
    !Array.isArray(data.diagnostics)
  ) {
    return [];
  }
  return data.diagnostics.flatMap((raw): IrDiagnostic[] => {
    if (!raw || typeof raw !== 'object') return [];
    const d = raw as Record<string, unknown>;
    if (typeof d['message'] !== 'string' || typeof d['code'] !== 'string') return [];
    const str = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : null);
    return [
      {
        severity: SEVERITIES.has(d['severity'] as string) ? (d['severity'] as IrDiagnostic['severity']) : 'error',
        code: d['code'] as string,
        message: d['message'] as string,
        instance_id: str('instance_id'),
        block_ref: str('block_ref'),
        slot_key: str('slot_key'),
      },
    ];
  });
}
